import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import {
  Table,
  Input,
  Space,
  Tag,
  Avatar,
  Button,
  Card,
  Skeleton,
  App as AntApp,
  Tooltip,
  DatePicker,
  Drawer,
  Empty,
  Statistic,
  Modal,
  Typography,
} from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  EyeOutlined,
  SafetyCertificateOutlined,
  BellOutlined,
  HistoryOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { t } from '../../i18n';
import { apiClient } from '../../lib/api-client';

interface InsuranceRow {
  id: number;
  make: string;
  model: string;
  plate: string;
  color: string;
  // Tech passport (texpassport) series + number. Nullable — only set for
  // cars whose tech passport was entered (OSAGO flow / admin vehicle-edit).
  techSery: string | null;
  techNumber: string | null;
  insuranceExpiry: string | null;
  // True when this is the driver's currently-active vehicle. Used for
  // the "Asosiy" badge — drivers can own multiple vehicles, only one
  // is selected at a time for trip matching.
  isSelected: boolean;
  // Server-computed: positive = days remaining, 0 = today, negative =
  // expired N days ago, null = no insurance date on file.
  daysLeft: number | null;
  reminderCount: number;
  lastReminderSentAt: string | null;
  driver: {
    id: number;
    fullName: string | null;
    phone: string;
    photoUrl: string | null;
    status: 'ONLINE' | 'OFFLINE' | 'BLOCKED' | 'RESTING';
    isDriverVerified: boolean;
  };
}

interface NotificationLogEntry {
  id: number;
  sentAt: string;
  reminderType: 'MANUAL' | 'AUTO_2W' | 'AUTO_1W' | 'AUTO_1D';
  daysLeftAtSend: number | null;
  fcmDelivered: boolean;
  sentBy: { id: number; username: string; fullName: string | null } | null;
}

const statusColors: Record<string, string> = {
  ONLINE: 'green',
  OFFLINE: 'default',
  BLOCKED: 'red',
  RESTING: 'orange',
};

const statusLabels: Record<string, string> = {
  ONLINE: t('status.online'),
  OFFLINE: t('status.offline'),
  BLOCKED: t('status.blocked'),
  RESTING: t('status.resting'),
};

// Days-left badge — gray "no data" when the driver hasn't given us the
// insurance date yet, otherwise color-coded by urgency.
const renderExpiryBadge = (daysLeft: number | null) => {
  if (daysLeft === null) {
    return (
      <Tag color="default" icon={<SafetyCertificateOutlined />}>
        {t('insurance.noData')}
      </Tag>
    );
  }
  if (daysLeft < 0) {
    return (
      <Tag color="red">
        {t('insurance.expiredPrefix')} {Math.abs(daysLeft)} {t('insurance.daysSuffix')}
      </Tag>
    );
  }
  if (daysLeft === 0) return <Tag color="red">{t('insurance.expiresToday')}</Tag>;
  if (daysLeft <= 3) return <Tag color="red">{daysLeft} {t('insurance.daysLeftSuffix')}</Tag>;
  if (daysLeft <= 7) return <Tag color="orange">{daysLeft} {t('insurance.daysLeftSuffix')}</Tag>;
  if (daysLeft <= 14) return <Tag color="gold">{daysLeft} {t('insurance.daysLeftSuffix')}</Tag>;
  return <Tag color="green">{daysLeft} {t('insurance.daysLeftSuffix')}</Tag>;
};

const reminderTypeLabel = (type: NotificationLogEntry['reminderType']): string => {
  switch (type) {
    case 'MANUAL':
      return t('insurance.reminderManual');
    case 'AUTO_2W':
      return t('insurance.reminderAuto2w');
    case 'AUTO_1W':
      return t('insurance.reminderAuto1w');
    case 'AUTO_1D':
      return t('insurance.reminderAuto1d');
  }
};

const reminderTypeColor = (type: NotificationLogEntry['reminderType']): string => {
  switch (type) {
    case 'MANUAL':
      return 'blue';
    case 'AUTO_2W':
      return 'gold';
    case 'AUTO_1W':
      return 'orange';
    case 'AUTO_1D':
      return 'red';
  }
};

export const InsuranceList = () => {
  const { show } = useNavigation();
  const { message } = AntApp.useApp();
  const [searchText, setSearchText] = useState('');
  const [notifyingId, setNotifyingId] = useState<number | null>(null);
  const [savingVehicleId, setSavingVehicleId] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyVehicleId, setHistoryVehicleId] = useState<number | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEntries, setHistoryEntries] = useState<NotificationLogEntry[]>([]);

  // "Compose & send" modal state. Operator can edit the prefilled
  // title/body in their preferred language before pushing. The default
  // text uses Uzbek templates here to match what `notifyInsuranceExpiry`
  // produces server-side when no override is given (driver's
  // preferredLang controls server-side default; this is just the
  // preview/edit field).
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeRow, setComposeRow] = useState<InsuranceRow | null>(null);
  const [composeTitle, setComposeTitle] = useState('');
  const [composeBody, setComposeBody] = useState('');

  const { tableProps, setFilters, tableQueryResult } = useTable<InsuranceRow>({
    resource: 'admin/insurance',
    syncWithLocation: true,
  });

  // The aggregate counter — drivers reminded in the last 7 days — is
  // returned in the meta block alongside pagination so we don't need a
  // second request.
  const remindedThisWeek =
    (tableQueryResult.data?.['meta'] as { remindedThisWeekUniqueDrivers?: number } | undefined)
      ?.remindedThisWeekUniqueDrivers ?? 0;

  const saveExpiry = async (vehicleId: number, date: Dayjs | null) => {
    setSavingVehicleId(vehicleId);
    try {
      await apiClient.put(`/admin/vehicles/${vehicleId}`, {
        insuranceExpiry: date ? date.format('YYYY-MM-DD') : null,
      });
      message.success(t('insurance.savedExpiry'));
      tableQueryResult.refetch();
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('insurance.saveFailed'));
    } finally {
      setSavingVehicleId(null);
    }
  };

  // Build the default Uzbek title/body the operator will see prefilled
  // in the compose modal. Mirrors the server's UZ template — when the
  // operator submits without editing, the server picks the same text in
  // the driver's preferred language. Edits override that.
  const buildDefaultPushText = (row: InsuranceRow): { title: string; body: string } => {
    const title = "🚗 Sug'urta muddati haqida eslatma";
    const plate = row.plate;
    if (row.daysLeft === null) {
      return {
        title,
        body: `🚗 ${plate}: sug'urta ma'lumoti yo'q. Iltimos, ma'muriyatga murojaat qiling.`,
      };
    }
    if (row.daysLeft < 0) {
      return {
        title,
        body: `🚗 ${plate}: sug'urta muddati ${Math.abs(row.daysLeft)} kun oldin tugagan. Yangi sug'urtani hoziroq Safaro ilovasidan xarid qiling.`,
      };
    }
    if (row.daysLeft === 0) {
      return {
        title,
        body: `🚗 ${plate}: sug'urta muddati bugun tugaydi. Yangi sug'urtani hoziroq Safaro ilovasidan xarid qiling.`,
      };
    }
    return {
      title,
      body: `🚗 ${plate}: sug'urta ${row.daysLeft} kundan keyin tugaydi. Yangi sug'urtani Safaro ilovasidan xarid qiling.`,
    };
  };

  const openCompose = (row: InsuranceRow) => {
    const tpl = buildDefaultPushText(row);
    setComposeRow(row);
    setComposeTitle(tpl.title);
    setComposeBody(tpl.body);
    setComposeOpen(true);
  };

  // The notify endpoint is keyed off the vehicle id so a driver with
  // multiple cars receives a reminder about the specific car the
  // operator clicked, not whichever happens to be flagged isSelected.
  const sendInsurancePush = async () => {
    if (!composeRow) return;
    const vehicleId = composeRow.id;
    setNotifyingId(vehicleId);
    try {
      const res = await apiClient.post<{ delivered: boolean; hasFcmToken: boolean }>(
        `/admin/insurance/${vehicleId}/notify`,
        {
          title: composeTitle.trim() || undefined,
          body: composeBody.trim() || undefined,
        },
      );
      if (res.data?.delivered) {
        message.success(t('insurance.pushSent'));
      } else if (!res.data?.hasFcmToken) {
        message.warning(t('insurance.pushNoToken'));
      } else {
        message.warning(t('insurance.pushFailed'));
      }
      setComposeOpen(false);
      setComposeRow(null);
      // Refetch the list so reminderCount and lastReminderSentAt update
      // for this row even when the push didn't actually reach the device
      // (we still log the attempt).
      tableQueryResult.refetch();
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('insurance.pushFailed'));
    } finally {
      setNotifyingId(null);
    }
  };

  const openHistory = async (vehicleId: number) => {
    setHistoryVehicleId(vehicleId);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await apiClient.get<{ data: NotificationLogEntry[] }>(
        `/admin/insurance/${vehicleId}/history`,
      );
      setHistoryEntries(res.data.data || []);
    } catch {
      setHistoryEntries([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const applySearch = (value: string) => {
    setSearchText(value);
    setFilters(value ? [{ field: 'search', operator: 'contains', value }] : []);
  };

  const columns = [
    {
      title: t('table.driver'),
      dataIndex: ['driver', 'fullName'],
      key: 'driver',
      render: (_: unknown, record: InsuranceRow) => (
        <Space>
          <Avatar src={record.driver.photoUrl} icon={<UserOutlined />} />
          <div>
            <Space size={6} wrap>
              <span>{record.driver.fullName || t('common.noName')}</span>
              {!record.driver.isDriverVerified && (
                <Tag color="default" style={{ margin: 0, fontSize: 11 }}>
                  {t('insurance.driverUnverified')}
                </Tag>
              )}
            </Space>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.driver.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: t('insurance.vehicle'),
      key: 'vehicle',
      render: (_: unknown, record: InsuranceRow) => (
        <Space direction="vertical" size={0}>
          <Space size={6} wrap>
            <span>{record.make} {record.model}</span>
            {record.isSelected && (
              <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
                {t('insurance.primaryVehicle')}
              </Tag>
            )}
          </Space>
          <div style={{ color: '#7F8C8D', fontSize: 12 }}>
            {/* Plate is copyable — copy icon sits right after the tag.
                stopPropagation so copying doesn't open the driver page. */}
            <span onClick={(e) => e.stopPropagation()}>
              <Typography.Text copyable={{ text: record.plate }}>
                <Tag style={{ marginRight: 4 }}>{record.plate}</Tag>
              </Typography.Text>
            </span>{' '}
            <span>{record.color}</span>
          </div>
        </Space>
      ),
    },
    {
      title: t('insurance.techSery'),
      key: 'techSery',
      width: 130,
      render: (_: unknown, record: InsuranceRow) =>
        record.techSery ? (
          <span onClick={(e) => e.stopPropagation()}>
            <Typography.Text copyable style={{ fontFamily: 'monospace' }}>
              {record.techSery}
            </Typography.Text>
          </span>
        ) : (
          <span style={{ color: '#BDC3C7' }}>—</span>
        ),
    },
    {
      title: t('insurance.techNumber'),
      key: 'techNumber',
      width: 150,
      render: (_: unknown, record: InsuranceRow) =>
        record.techNumber ? (
          <span onClick={(e) => e.stopPropagation()}>
            <Typography.Text copyable style={{ fontFamily: 'monospace' }}>
              {record.techNumber}
            </Typography.Text>
          </span>
        ) : (
          <span style={{ color: '#BDC3C7' }}>—</span>
        ),
    },
    {
      title: t('drivers.insuranceExpiry'),
      dataIndex: 'insuranceExpiry',
      key: 'insuranceExpiry',
      render: (iso: string | null, record: InsuranceRow) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DatePicker
            value={iso ? dayjs(iso) : null}
            format="DD.MM.YYYY"
            placeholder={t('insurance.pickDate')}
            disabled={savingVehicleId === record.id}
            onChange={(d) => saveExpiry(record.id, d)}
            style={{ width: 160 }}
          />
        </div>
      ),
    },
    {
      title: t('insurance.daysLeft'),
      key: 'daysLeft',
      // Server already sorted ascending by expiry — keep the column
      // as a non-sortable display so we don't fight the server order.
      render: (_: unknown, record: InsuranceRow) => renderExpiryBadge(record.daysLeft),
    },
    {
      title: t('insurance.lastReminder'),
      key: 'lastReminder',
      render: (_: unknown, record: InsuranceRow) =>
        record.lastReminderSentAt ? (
          <Space size={4}>
            <span style={{ color: '#7F8C8D', fontSize: 12 }}>
              {dayjs(record.lastReminderSentAt).format('DD.MM.YYYY HH:mm')}
            </span>
            <Tag color="blue" style={{ margin: 0 }}>
              {record.reminderCount}
            </Tag>
          </Space>
        ) : (
          <span style={{ color: '#BDC3C7' }}>—</span>
        ),
    },
    {
      title: t('table.status'),
      dataIndex: ['driver', 'status'],
      key: 'status',
      render: (_: unknown, record: InsuranceRow) => (
        <Tag color={statusColors[record.driver.status]}>
          {statusLabels[record.driver.status] || record.driver.status}
        </Tag>
      ),
    },
    {
      title: t('table.actions'),
      key: 'actions',
      render: (_: unknown, record: InsuranceRow) => (
        <Space size={0} onClick={(e) => e.stopPropagation()}>
          <Tooltip
            title={
              record.daysLeft === null ? t('insurance.cantNotifyNoData') : t('insurance.sendPush')
            }
          >
            <Button
              type="text"
              icon={<BellOutlined />}
              loading={notifyingId === record.id}
              disabled={record.daysLeft === null}
              onClick={() => openCompose(record)}
            />
          </Tooltip>
          <Tooltip title={t('insurance.history')}>
            <Button
              type="text"
              icon={<HistoryOutlined />}
              onClick={() => openHistory(record.id)}
            />
          </Tooltip>
          <Tooltip title={t('common.view')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => show('admin/drivers', record.driver.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <List
      title={t('insurance.title')}
      headerButtons={
        <Space>
          <Card
            size="small"
            styles={{ body: { padding: '6px 12px' } }}
            style={{ background: '#F5F8FF', borderColor: '#D6E4FF' }}
          >
            <Space size={8}>
              <ClockCircleOutlined style={{ color: '#1890FF' }} />
              <Statistic
                title={t('insurance.remindedThisWeek')}
                value={remindedThisWeek}
                valueStyle={{ fontSize: 16, color: '#1890FF' }}
              />
            </Space>
          </Card>
          <Input
            placeholder={t('common.search')}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={() => applySearch(searchText)}
            allowClear
            onClear={() => applySearch('')}
            style={{ width: 240 }}
          />
        </Space>
      }
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => show('admin/drivers', record.driver.id),
          style: { cursor: 'pointer' },
        })}
        loading={
          tableProps.loading
            ? { indicator: <Card bordered={false}><Skeleton active paragraph={{ rows: 8 }} /></Card> }
            : false
        }
      />

      <Modal
        title={
          composeRow ? (
            <Space>
              <BellOutlined />
              {t('insurance.sendPush')} — {composeRow.plate}
            </Space>
          ) : (
            t('insurance.sendPush')
          )
        }
        open={composeOpen}
        onCancel={() => {
          setComposeOpen(false);
          setComposeRow(null);
        }}
        onOk={sendInsurancePush}
        okText={t('common.send')}
        cancelText={t('common.cancel')}
        confirmLoading={notifyingId === composeRow?.id}
        okButtonProps={{ disabled: !composeBody.trim() }}
        width={520}
      >
        {/* Compose form. Both fields are pre-filled with the default
            template; operator can edit before sending. Empty values
            after trim cause the server to fall back to the
            language-specific default for that driver, so clearing the
            field doesn't accidentally send an empty push. */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 13, color: '#595959' }}>
            {t('insurance.pushTitleLabel')}
          </div>
          <Input
            value={composeTitle}
            onChange={(e) => setComposeTitle(e.target.value)}
            maxLength={120}
            showCount
          />
        </div>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: '#595959' }}>
            {t('insurance.pushBodyLabel')}
          </div>
          <Input.TextArea
            value={composeBody}
            onChange={(e) => setComposeBody(e.target.value)}
            autoSize={{ minRows: 3, maxRows: 6 }}
            maxLength={500}
            showCount
          />
        </div>
        {composeRow?.daysLeft != null && composeRow.daysLeft <= 7 && (
          <div
            style={{
              marginTop: 12,
              padding: '8px 12px',
              background: '#FFF1F0',
              border: '1px solid #FFCCC7',
              borderRadius: 6,
              color: '#A8071A',
              fontSize: 12,
            }}
          >
            {composeRow.daysLeft <= 0
              ? `Sug'urta muddati ${Math.abs(composeRow.daysLeft)} kun oldin tugagan`
              : `Sug'urta ${composeRow.daysLeft} kundan keyin tugaydi`}
          </div>
        )}
      </Modal>

      <Drawer
        title={t('insurance.historyTitle')}
        placement="right"
        width={420}
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setHistoryVehicleId(null);
          setHistoryEntries([]);
        }}
      >
        {historyLoading ? (
          <Skeleton active />
        ) : historyEntries.length === 0 ? (
          <Empty description={t('insurance.historyEmpty')} />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {historyEntries.map((entry) => (
              <Card key={entry.id} size="small" styles={{ body: { padding: 12 } }}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Space>
                    <Tag color={reminderTypeColor(entry.reminderType)} style={{ margin: 0 }}>
                      {reminderTypeLabel(entry.reminderType)}
                    </Tag>
                    <Tag color={entry.fcmDelivered ? 'green' : 'default'} style={{ margin: 0 }}>
                      {entry.fcmDelivered ? t('insurance.delivered') : t('insurance.notDelivered')}
                    </Tag>
                  </Space>
                  <span style={{ fontSize: 12, color: '#7F8C8D' }}>
                    {dayjs(entry.sentAt).format('DD.MM.YYYY HH:mm')}
                    {entry.daysLeftAtSend !== null && (
                      <>
                        {' · '}
                        {entry.daysLeftAtSend < 0
                          ? `${t('insurance.expiredPrefix')} ${Math.abs(entry.daysLeftAtSend)} ${t('insurance.daysSuffix')}`
                          : `${entry.daysLeftAtSend} ${t('insurance.daysLeftSuffix')}`}
                      </>
                    )}
                  </span>
                  {entry.sentBy && (
                    <span style={{ fontSize: 12 }}>
                      {entry.sentBy.fullName || entry.sentBy.username}
                    </span>
                  )}
                </Space>
              </Card>
            ))}
          </Space>
        )}
      </Drawer>

      {historyVehicleId !== null && null}
    </List>
  );
};
